import type React from "react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid } from "@/components/nzDataGrid"
import { useFormatCurrency } from "@/formatters/use-format-currency"
import { Button } from "@/nzds/inputs/button"

import { ArrowBackRounded } from "@mui/icons-material"
import { Box, CircularProgress, Stack, Typography } from "@mui/material"

import { useInsightsNavigate } from "../data/use-insights-navigate"
import { Card, Eyebrow, SectionTitle } from "../homepage/atoms"
import { actionColumns } from "./action-columns"
import { ActionsFilterBar } from "./actions-filter-bar"
import {
  type ConfidenceFilter,
  type DetectorFilter,
  filterActionRows,
  humanizeDetector,
} from "./filter-action-rows"
import { useActionRows } from "./use-action-rows"

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card sx={{ p: "14px 18px" }}>
    <Eyebrow>{label}</Eyebrow>
    <Typography sx={{ fontWeight: "bold", mt: 0.25 }} variant="h2">
      {value}
    </Typography>
  </Card>
)

// Hoisted so `useFormatCurrency`'s memoized callback stays referentially stable
// across renders — otherwise `columns = useMemo([formatCurrency])` rebuilds
// every render and NzDataGrid re-processes its column defs each time.
const CURRENCY_OPTS = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
} as const

export const RecommendedActions: React.FC = () => {
  const navigate = useInsightsNavigate()
  const { rows: allRows, isPending } = useActionRows()
  const { formatCurrency } = useFormatCurrency(CURRENCY_OPTS)
  const { t } = useTranslation("common")

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments -- DetectorFilter is structurally `string` (the "ALL" | string union), so the explicit type argument is retained for self-documentation.
  const [detector, setDetector] = useState<DetectorFilter>("ALL")
  const [confidence, setConfidence] = useState<ConfidenceFilter>("ALL")
  const [minDollars, setMinDollars] = useState(0)

  // Unique detectors as {code (stable filter value), label (human name)}.
  const detectors = useMemo(() => {
    const byCode = new Map<string, string>()
    for (const r of allRows) {
      if (!byCode.has(r.detector_code)) {
        byCode.set(r.detector_code, humanizeDetector(r.detector_label))
      }
    }
    return [...byCode].map(([code, label]) => ({ code, label }))
  }, [allRows])
  const maxDollars = useMemo(
    () => allRows.reduce((m, r) => Math.max(m, r.impact_dollars), 0),
    [allRows]
  )
  const rows = useMemo(
    () => filterActionRows(allRows, detector, confidence, minDollars),
    [allRows, detector, confidence, minDollars]
  )
  const columns = useMemo(
    () =>
      actionColumns({
        formatCurrency,
        onReview: (row) => {
          // Detail is a child of the actions aggregator:
          // /neat/actions/schedule-misalignment/:siteId — navigate relative to
          // the current /neat/actions route (no "../").
          navigate(`${row.detector_path}/${row.site_id}`)
        },
        t,
      }),
    [formatCurrency, navigate, t]
  )

  const totalDollars = allRows.reduce((sum, r) => sum + r.impact_dollars, 0)
  // Dedupe on site_id — labels aren't guaranteed unique/stable across sites.
  const sitesAffected = new Set(allRows.map((r) => r.site_id)).size

  const backButton = (
    <Button
      onClick={() => {
        navigate("..")
      }}
      size="small"
      startIcon={<ArrowBackRounded />}
      variant="text"
    >
      Back to Insights
    </Button>
  )

  if (isPending) {
    return (
      <Box sx={{ maxWidth: 1304, margin: "0 auto", p: 3 }}>
        {backButton}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", py: 6, justifyContent: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary" variant="body2">
            Loading recommended actions…
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1304, margin: "0 auto", p: 3 }}>
      <Stack spacing={3}>
        <Box>
          {backButton}
          <Eyebrow>Portfolio</Eyebrow>
          <SectionTitle>Recommended actions</SectionTitle>
        </Box>

        {allRows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4 }} variant="body2">
            Portfolio is all clear — no open actions.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1.5,
              }}
            >
              <Kpi label="Open actions" value={`${allRows.length}`} />
              <Kpi
                label={t("neat.observedLabel")}
                value={formatCurrency(totalDollars)}
              />
              <Kpi label="Sites affected" value={`${sitesAffected}`} />
            </Box>

            <ActionsFilterBar
              confidence={confidence}
              detector={detector}
              detectors={detectors}
              maxDollars={maxDollars}
              minDollars={minDollars}
              onConfidenceChange={setConfidence}
              onDetectorChange={setDetector}
              onMinDollarsChange={setMinDollars}
            />

            <Box sx={{ height: 640, width: "100%" }}>
              <NzDataGrid
                aria-label="Recommended actions across portfolio"
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => (row as { id: string }).id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "impact_dollars", sort: "desc" }],
                  },
                  pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
                pageSizeOptions={[25, 50]}
                pagination
                rows={rows}
              />
            </Box>

            <Typography color="text.secondary" variant="caption">
              {t("neat.actionsCaption", {
                shown: rows.length,
                total: allRows.length,
              })}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  )
}
